import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Document | null} */
  #cachedProductHtml = null;
  /** @type {string | null} The URL the cached HTML belongs to. */
  #cachedProductUrl = null;
  /** @type {string | null} Explicitly selected product URL (set when a color swatch is chosen). */
  #selectedProductUrl = null;

  get cachedProductHtml() {
    return this.#cachedProductHtml;
  }

  /**
   * Sets the product the quick add should target (e.g. when a color swatch is selected)
   * and clears any cached/prefetched HTML so the next quick add fetches the new product.
   * @param {string} url - The selected variant product URL.
   */
  setSelectedProduct(url) {
    this.#selectedProductUrl = url || null;
    this.resetCachedProductHtml();
  }

  /**
   * Clears the cached product page HTML so the next quick add re-fetches.
   * Used when the product card switches to a different variant product (e.g. color swatch).
   */
  resetCachedProductHtml() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#cachedProductHtml = null;
    this.#cachedProductUrl = null;
  }

  get productPageUrl() {
    // Prefer the explicitly selected variant product (set on swatch click); fall back to the card link.
    if (this.#selectedProductUrl) return this.#selectedProductUrl;

    return /** @type {HTMLAnchorElement} */ (this.closest('product-card')?.querySelector('a[ref="productCardLink"]'))
      ?.href;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Color-swatch flow only: when a variant product was selected via a product-card swatch,
    // use the robust path that targets the exact selected product. Otherwise fall through to
    // the original, unchanged quick add behavior so nothing else is affected.
    if (this.#selectedProductUrl) {
      await this.#handleSwatchClick(event);
      return;
    }

    if (!this.#cachedProductHtml) {
      await this.fetchProductPage(this.productPageUrl);
    }

    if (this.#cachedProductHtml) {
      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(
        this.#cachedProductHtml.documentElement.outerHTML,
        'text/html'
      );

      await this.updateQuickAddModal(freshHtmlCopy);
    }

    this.#openQuickAddModal();
  };

  /**
   * Quick add click handler used only when a product-card color swatch has selected a variant product.
   * Disables the button until the selected product loads, and opens the modal only on success.
   * @param {Event} event - The click event.
   */
  async #handleSwatchClick(event) {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    const url = this.productPageUrl;
    if (!url) return;

    // Cancel any in-flight hover prefetch so it can't race / abort our authoritative fetch.
    this.#abortController?.abort();
    this.#abortController = null;

    this.#setButtonLoading(button, true);

    try {
      // Only trust the cache if it belongs to the currently selected variant product.
      const cacheIsFresh = this.#cachedProductHtml && this.#cachedProductUrl === url;

      const html = cacheIsFresh ? this.#cachedProductHtml : await this.#loadProductPage(url);

      // Guard against another swatch change mid-fetch repointing to a different product.
      if (this.productPageUrl !== url) return;
      if (!html) return;

      // Create a fresh copy of the cached HTML to avoid modifying the original
      const freshHtmlCopy = new DOMParser().parseFromString(html.documentElement.outerHTML, 'text/html');

      await this.updateQuickAddModal(freshHtmlCopy);
      this.#openQuickAddModal();
    } catch (error) {
      console.error('Quick add failed to load product:', error);
    } finally {
      this.#setButtonLoading(button, false);
    }
  }

  /**
   * Toggles the loading/disabled state on the quick add button (swatch flow only).
   * @param {HTMLButtonElement | null} button - The quick add button.
   * @param {boolean} loading - Whether a fetch is in progress.
   */
  #setButtonLoading(button, loading) {
    if (!button) return;
    button.disabled = loading;
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    button.classList.toggle('quick-add__trigger--loading', loading);
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    const cartDialog = this.closest('cart-drawer-component');
    if (cartDialog && typeof cartDialog.close === 'function') {
      cartDialog.close();
    }

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<void>}
   * @throws {Error} If the fetch request fails or returns a non-200 response
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      // Store the HTML for later use, tagged with the URL it belongs to
      this.#cachedProductHtml = html;
      this.#cachedProductUrl = productPageUrl;
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Performs a fetch + parse + cache for the swatch flow only. Not abortable by prefetches.
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document>} The parsed product page document
   * @throws {Error} If the fetch fails or returns a non-200 response
   */
  async #loadProductPage(productPageUrl) {
    const response = await fetch(productPageUrl);

    if (!response.ok) {
      throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
    }

    const responseText = await response.text();
    const html = new DOMParser().parseFromString(responseText, 'text/html');

    // Store the HTML for later use, tagged with the URL it belongs to
    this.#cachedProductHtml = html;
    this.#cachedProductUrl = productPageUrl;

    return html;
  }

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   */
  async updateQuickAddModal(newHtml) {
    const productGrid = newHtml.querySelector('[data-product-grid-content]');
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Use innerHTML for full replacement to ensure images are properly rendered
    modalContent.innerHTML = productGrid.innerHTML;
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;

    // Update inventory status in quick add modal
    const newInventoryElement = event.detail.data.html?.querySelector('.quick-add-inventory');
    const currentInventoryElement = this.querySelector('.quick-add-inventory');

    if (newInventoryElement && currentInventoryElement) {
      morph(currentInventoryElement, newInventoryElement);
    }

    // Update product-inventory components in quick add modal
    const newProductInventoryElements = event.detail.data.html?.querySelectorAll('product-inventory');
    const currentProductInventoryElements = this.querySelectorAll('product-inventory');

    if (newProductInventoryElements && currentProductInventoryElements) {
      newProductInventoryElements.forEach((/** @type {Element} */ newElement, /** @type {number} */ index) => {
        const currentElement = currentProductInventoryElements[index];
        if (currentElement) {
          morph(currentElement, newElement);
        }
      });
    }
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
